/**
 * dashboard.service.js — Dashboard domain service.
 * Notifications, KPIs, trends, site summaries.
 */
import {
  toApiUrl,
  fetchWithAuthRetry,
  jsonFetchOptions,
  parseJsonOrThrow,
  readApiErrorMessage,
  handleUnauthorizedResponse,
  normalizeApiList,
  isLicenseGatedMessage,
  isActiveRunningScan,
  isNetworkScheduleEndpointMarkedMissing,
  setNetworkScheduleEndpointMarkedMissing,
  getNetworkScheduleRequestPromise,
  setNetworkScheduleRequestPromise,
} from './_core';

// — Dashboard notification normalizer (private) —

function normalizeDashboardNotificationItem(item, index = 0) {
  const raw = item && typeof item === 'object' ? item : {};
  const idRaw = raw.id ?? raw.uuid ?? raw.notification_id ?? `notification-${index + 1}`;
  const id = String(idRaw || '').trim() || `notification-${index + 1}`;
  const title = String(raw.title || raw.subject || raw.verb || 'Notification').trim() || 'Notification';
  const message = String(raw.message || raw.text || raw.description || '').trim();
  const read = typeof raw.read === 'boolean' ? raw.read : !Boolean(raw.unread);
  const link = String(raw.link || raw.url || raw.href || '').trim();
  const createdAt = raw.createdAt || raw.created_at || raw.timestamp || null;
  const severity = String(raw.severity || raw.level || 'info').trim().toLowerCase() || 'info';
  const sourceEventType = String(
    raw.sourceEventType || raw.source_event_type || raw.event_type || raw.type || raw.verb || ''
  ).trim();
  const sourceObjectId = String(
    raw.sourceObjectId || raw.source_object_id || raw.object_id || raw.target_id || ''
  ).trim();
  const sourceType = String(raw.sourceType || raw.source_type || '').trim();
  const scanType = String(
    raw.scanType || raw.scan_type || raw?.metadata?.scan_type || raw?.auditMetadata?.scan_type || ''
  ).trim();

  return {
    id,
    title,
    message: message || title,
    read,
    link,
    createdAt,
    severity,
    sourceEventType,
    sourceObjectId,
    sourceType,
    scanType,
  };
}

export async function getDashboardNotifications(token = null, query = null) {
  const qs =
    query && typeof query === 'object' && Object.keys(query).length > 0
      ? `?${new URLSearchParams(query).toString()}`
      : '';

  const response = await fetchWithAuthRetry(toApiUrl(`/notifications/${qs}`), jsonFetchOptions(token), token);
  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Notifications fetch failed (${response.status})`);
    throw new Error(msg);
  }

  const data = await parseJsonOrThrow(response, 'Dashboard notifications');
  const itemsRaw = normalizeApiList(data?.items ?? data);
  const items = itemsRaw.map((item, index) => normalizeDashboardNotificationItem(item, index));

  const unreadFromApi = Number(data?.unreadCount ?? data?.unread_count);
  const unreadCount = Number.isFinite(unreadFromApi)
    ? Math.max(0, Math.floor(unreadFromApi))
    : items.filter((item) => !item.read).length;

  return { unreadCount, items };
}

export async function getDashboardNotificationUnreadCount(token = null) {
  const response = await fetchWithAuthRetry(toApiUrl('/notifications/unread-count/'), jsonFetchOptions(token), token);
  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Unread notification count fetch failed (${response.status})`);
    throw new Error(msg);
  }

  const data = await parseJsonOrThrow(response, 'Dashboard notification unread count');
  const unread = Number(data?.unreadCount ?? data?.unread_count);
  return { unreadCount: Number.isFinite(unread) ? Math.max(0, Math.floor(unread)) : 0 };
}

export async function markDashboardNotificationRead(notificationId, token = null) {
  const id = String(notificationId || '').trim();
  if (!id) throw new Error('Notification id is required.');

  const response = await fetchWithAuthRetry(
    toApiUrl(`/notifications/${encodeURIComponent(id)}/read/`),
    {
      method: 'POST',
      ...jsonFetchOptions(token),
      body: JSON.stringify({}),
    },
    token,
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Mark notification read failed (${response.status})`);
    throw new Error(msg);
  }

  return parseJsonOrThrow(response, 'Mark notification read');
}

export async function markAllDashboardNotificationsRead(token = null) {
  const response = await fetchWithAuthRetry(
    toApiUrl('/notifications/mark-all-read/'),
    {
      method: 'POST',
      ...jsonFetchOptions(token),
      body: JSON.stringify({}),
    },
    token,
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Mark all notifications read failed (${response.status})`);
    throw new Error(msg);
  }

  return parseJsonOrThrow(response, 'Mark all notifications read');
}

/**
 * Detect Overview KPI `?range=` support.
 * Friend's date-range API returns 400 for unknown tokens; older portals ignore
 * `range` and still return 200 — use client windowing until that ships.
 */
export async function supportsOverviewDateRange(token = null) {
  try {
    const response = await fetchWithAuthRetry(
      toApiUrl('/dashboard/overview/kpis/?range=__unsupported__'),
      jsonFetchOptions(token),
      token
    );
    return response.status === 400;
  } catch {
    return false;
  }
}

export async function getDashboardSummary(token = null, query = null) {
  let kpis = {};
  let runningScans = [];
  let scheduledScans = [];
  const opts = jsonFetchOptions(token);
  // KPIs support ?range=; running/schedule endpoints ignore it — keep range only on KPIs.
  const queryObj = query && typeof query === 'object' ? { ...query } : {};
  const rangeValue = String(queryObj.range ?? '').trim();
  const scopeOnly = { ...queryObj };
  delete scopeOnly.range;
  const qsScope =
    Object.keys(scopeOnly).length > 0
      ? `?${new URLSearchParams(scopeOnly).toString()}`
      : '';
  const kpiParams = { ...scopeOnly };
  if (rangeValue) kpiParams.range = rangeValue;
  const qsKpis =
    Object.keys(kpiParams).length > 0
      ? `?${new URLSearchParams(kpiParams).toString()}`
      : '';

  let kpiFetchError = null;
  let kpiUnauthorized = false;
  try {
    const rKpis = await fetchWithAuthRetry(toApiUrl(`/dashboard/overview/kpis/${qsKpis}`), opts, token);
    if (rKpis.ok) {
      kpis = await parseJsonOrThrow(rKpis, 'Dashboard KPI');
    } else {
      await handleUnauthorizedResponse(rKpis);
      if (rKpis.status === 401) {
        kpiUnauthorized = true;
      } else {
        kpiFetchError = await readApiErrorMessage(rKpis, 'Unable to load overview metrics.');
      }
    }
  } catch (err) {
    kpiFetchError = err?.message ? String(err.message) : 'Unable to load overview metrics.';
  }
  try {
    const rRun = await fetchWithAuthRetry(toApiUrl(`/dashboard/scans/running/${qsScope}`), opts, token);
    if (rRun.ok) {
      const runningData = await parseJsonOrThrow(rRun, 'Running scans');
      runningScans = normalizeApiList(runningData).filter(isActiveRunningScan);
    }
    else await handleUnauthorizedResponse(rRun);
  } catch {
    /* empty */
  }
  try {
    if (!isNetworkScheduleEndpointMarkedMissing()) {
      let schedulePromise = getNetworkScheduleRequestPromise();
      if (!schedulePromise) {
        schedulePromise = fetchWithAuthRetry(toApiUrl(`/networkscan/schedule/${qsScope}`), opts, token)
          .finally(() => {
            setNetworkScheduleRequestPromise(null);
          });
        setNetworkScheduleRequestPromise(schedulePromise);
      }

      const rSched = await schedulePromise;
      if (rSched.ok) {
        setNetworkScheduleEndpointMarkedMissing(false);
        const scheduleData = await parseJsonOrThrow(rSched, 'Scan schedules');
        scheduledScans = Array.isArray(scheduleData) ? scheduleData : [];
      } else {
        if (rSched.status === 404) {
          try {
            const detail = await parseJsonOrThrow(rSched, 'Scan schedules');
            const noScans =
              typeof detail?.message === 'string' &&
              detail.message.toLowerCase().includes('no scans found');
            if (noScans) {
              setNetworkScheduleEndpointMarkedMissing(false);
              scheduledScans = [];
            } else {
              setNetworkScheduleEndpointMarkedMissing(true);
            }
          } catch {
            setNetworkScheduleEndpointMarkedMissing(true);
          }
        }
        await handleUnauthorizedResponse(rSched);
      }
    }
  } catch {
    /* empty */
  }

  const allOpen = kpis?.all_open || {};
  const criticalCount = Number(allOpen.critical ?? kpis.critical_count_site ?? kpis.critical ?? 0) || 0;
  const highCount = Number(allOpen.high ?? kpis.high_count_site ?? kpis.high ?? 0) || 0;
  const mediumCount = Number(allOpen.medium ?? kpis.medium_count_site ?? kpis.medium ?? 0) || 0;
  const lowCount = Number(allOpen.low ?? kpis.low_count_site ?? kpis.low ?? 0) || 0;

  const totalOpen =
    Number(kpis.total_open ?? kpis.total_count_site ?? (criticalCount + highCount + mediumCount + lowCount)) || 0;
  const totalClosed = Number(kpis.total_closed ?? kpis.close_count_site ?? 0) || 0;
  const totalAccepted = Number(kpis.total_accepted ?? kpis.accepted_count_site ?? 0) || 0;
  const totalFalsePositive = Number(kpis.total_false_positive ?? kpis.false_count_site ?? 0) || 0;
  const totalWeb = Number(kpis.total_web ?? kpis.web_count_site ?? 0) || 0;
  const totalNetwork = Number(kpis.total_network ?? kpis.net_count_site ?? 0) || 0;

  return {
    ...kpis,
    critical_count_site: criticalCount,
    high_count_site: highCount,
    medium_count_site: mediumCount,
    low_count_site: lowCount,
    close_count_site: totalClosed,
    accepted_count_site: totalAccepted,
    false_count_site: totalFalsePositive,
    false_positive_count_site: totalFalsePositive,
    web_count_site: totalWeb,
    net_count_site: totalNetwork,
    total_count_site: totalOpen,
    in_progress_scans: runningScans.length,
    scheduled_scans: scheduledScans.length,
    health_score: kpis.health_score ?? kpis.healthScore,
    health_grade: kpis.health_grade ?? kpis.healthGrade,
    health_label: kpis.health_label ?? kpis.healthLabel,
    health_formula_version: kpis.health_formula_version ?? kpis.healthFormulaVersion,
    health_top_drivers: kpis.health_top_drivers ?? kpis.healthTopDrivers,
    ...(kpiFetchError ? { _dashboardKpisError: kpiFetchError } : {}),
    ...(kpiUnauthorized ? { _dashboardKpisUnauthorized: true } : {}),
  };
}

export async function getHistoryData(token = null, query = null) {
  const qs =
    query && typeof query === 'object' && Object.keys(query).length > 0
      ? `?${new URLSearchParams(query).toString()}`
      : '';
  const primaryResponse = await fetchWithAuthRetry(
    toApiUrl(`/dashboard/trends/vulnerabilities/${qs}`),
    jsonFetchOptions(token),
    token
  );
  if (!primaryResponse.ok) {
    const msg = await readApiErrorMessage(primaryResponse, `History data failed (${primaryResponse.status})`);
    if (isLicenseGatedMessage(msg)) return null;
    throw new Error(msg);
  }

  return parseJsonOrThrow(primaryResponse, 'Trend vulnerabilities');
}

export async function getTopVulnerableHosts(token = null, query = null) {
  const params = new URLSearchParams();
  if (query && typeof query === 'object') {
    Object.entries(query).forEach(([key, value]) => {
      const normalized = String(value ?? '').trim();
      if (normalized) params.set(key, normalized);
    });
  }

  const qs = params.toString() ? `?${params.toString()}` : '';
  const response = await fetchWithAuthRetry(toApiUrl(`/dashboard/hosts/top-vulnerable/${qs}`), jsonFetchOptions(token), token);
  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Top vulnerable hosts failed (${response.status})`);
    throw new Error(msg);
  }

  const data = await parseJsonOrThrow(response, 'Top vulnerable hosts');
  return normalizeApiList(data);
}

export async function getSiteSummary(siteUuid, token = null) {
  const safeSiteUuid = String(siteUuid || '').trim();
  if (!safeSiteUuid) {
    throw new Error('Site UUID is required');
  }

  const response = await fetchWithAuthRetry(
    toApiUrl(`/dashboard/sites/${encodeURIComponent(safeSiteUuid)}/summary/`),
    jsonFetchOptions(token),
    token
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Site summary failed (${response.status})`);
    throw new Error(msg);
  }

  return parseJsonOrThrow(response, 'Site summary');
}
